import { useEffect, useState } from 'react';
import { API_URL } from '../../config/api';
import { readApiJson } from '../../utils/apiErrors';

// ── ManageCategoriesModal — popup taxonomy manager for Document Category /
// Sub-Category, opened from the 🗂️ Manage Categories button in Manage Legal
// Master. An accordion (not a two-panel table) so adding, renaming, and
// deleting a category and its sub-categories all happen in one place without
// a page navigation — every value defined here is what the Add/Edit Record
// form and the top filter bar's Document Category / Sub-Category dropdowns
// are sourced from, so changes are reflected immediately via onChanged.

const EditIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
);
const DeleteIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
);
const ChevronIcon = ({ open }) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s ease' }}><polyline points="9 6 15 12 9 18"/></svg>
);
const PlusIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14"/><path d="M5 12h14"/></svg>
);

// Compact inline name field with its own Save/Cancel — used for both "add
// category", "rename category", and "add/rename sub-category" so every
// mutation happens as a small inline row instead of a separate screen.
function InlineNameField({ initial = '', placeholder, autoFocus, onCancel, onSubmit }) {
    const [value, setValue] = useState(initial);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const submit = async () => {
        if (!value.trim()) { setError('Name is required.'); return; }
        setSaving(true); setError('');
        try {
            await onSubmit(value.trim());
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const onKeyDown = (e) => {
        if (e.key === 'Enter') submit();
        if (e.key === 'Escape') onCancel();
    };

    return (
        <div className="legal-cat-inline-form">
            <input
                className="legal-cat-inline-input"
                value={value}
                placeholder={placeholder}
                autoFocus={autoFocus}
                onChange={e => setValue(e.target.value)}
                onKeyDown={onKeyDown}
                disabled={saving}
            />
            <button type="button" className="legal-btn-save legal-cat-inline-btn" disabled={saving} onClick={submit}>{saving ? '…' : 'Save'}</button>
            <button type="button" className="legal-btn-cancel legal-cat-inline-btn" disabled={saving} onClick={onCancel}>Cancel</button>
            {error && <div className="legal-form-error legal-cat-inline-error">{error}</div>}
        </div>
    );
}

function SubcategoryRow({ sub, editing, onStartEdit, onCancelEdit, onRename, onDelete, deleteBusy }) {
    if (editing) {
        return (
            <div className="legal-cat-sub-row">
                <InlineNameField initial={sub.name} placeholder="Sub-category name" autoFocus onCancel={onCancelEdit} onSubmit={onRename} />
            </div>
        );
    }
    return (
        <div className="legal-cat-sub-row">
            <span className="legal-cat-sub-name">{sub.name}</span>
            <span className="legal-row-actions">
                <button className="legal-action-btn" title="Rename" onClick={onStartEdit}><EditIcon /></button>
                <button className="legal-action-btn legal-action-danger" title="Delete" disabled={deleteBusy} onClick={onDelete}>
                    {deleteBusy ? '…' : <DeleteIcon />}
                </button>
            </span>
        </div>
    );
}

function CategoryRow({
    cat, expanded, onToggle,
    editing, onStartEdit, onCancelEdit, onRename, onDelete, deleteBusy,
    addingSub, onStartAddSub, onCancelAddSub, onAddSub,
    editingSubId, onStartEditSub, onCancelEditSub, onRenameSub, onDeleteSub, deleteSubBusyId,
}) {
    return (
        <div className="legal-cat-card">
            <div className="legal-cat-header" onClick={() => !editing && onToggle()}>
                <span className="legal-cat-chevron"><ChevronIcon open={expanded} /></span>
                {editing ? (
                    <div className="legal-cat-inline-form" onClick={e => e.stopPropagation()}>
                        <InlineNameField initial={cat.name} placeholder="Category name" autoFocus onCancel={onCancelEdit} onSubmit={onRename} />
                    </div>
                ) : (
                    <>
                        <span className="legal-cat-name">{cat.name}</span>
                        <span className="legal-cat-count">{cat.subcategories.length} sub-cat{cat.subcategories.length === 1 ? '' : 's'}</span>
                        <span className="legal-row-actions" onClick={e => e.stopPropagation()}>
                            <button className="legal-action-btn" title="Rename category" onClick={onStartEdit}><EditIcon /></button>
                            <button className="legal-action-btn legal-action-danger" title="Delete category" disabled={deleteBusy} onClick={onDelete}>
                                {deleteBusy ? '…' : <DeleteIcon />}
                            </button>
                        </span>
                    </>
                )}
            </div>
            {expanded && (
                <div className="legal-cat-sub-list">
                    {cat.subcategories.map(sub => (
                        <SubcategoryRow
                            key={sub.id}
                            sub={sub}
                            editing={editingSubId === sub.id}
                            onStartEdit={() => onStartEditSub(sub.id)}
                            onCancelEdit={onCancelEditSub}
                            onRename={name => onRenameSub(sub.id, name)}
                            onDelete={() => onDeleteSub(sub)}
                            deleteBusy={deleteSubBusyId === sub.id}
                        />
                    ))}
                    {!cat.subcategories.length && !addingSub && (
                        <div className="legal-cat-sub-empty">No sub-categories yet.</div>
                    )}
                    {addingSub ? (
                        <div className="legal-cat-sub-row">
                            <InlineNameField placeholder="New sub-category name" autoFocus onCancel={onCancelAddSub} onSubmit={onAddSub} />
                        </div>
                    ) : (
                        <button type="button" className="legal-cat-add-sub-btn" onClick={onStartAddSub}>
                            <PlusIcon /> Add Sub-Category
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

function ManageCategoriesModal({ canManageData = false, onClose, onChanged }) {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [expandedIds, setExpandedIds] = useState(() => new Set());
    const [addingCategory, setAddingCategory] = useState(false);
    const [editingCategoryId, setEditingCategoryId] = useState(null);
    const [deleteCategoryBusyId, setDeleteCategoryBusyId] = useState(null);
    const [addingSubFor, setAddingSubFor] = useState(null);
    const [editingSubId, setEditingSubId] = useState(null);
    const [deleteSubBusyId, setDeleteSubBusyId] = useState(null);

    const load = async () => {
        setLoading(true); setError('');
        try {
            const res = await fetch(`${API_URL}/api/legal/categories/`, { credentials: 'include' });
            const body = await readApiJson(res, 'Could not load categories.');
            setCategories(body.categories || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const postJson = async (url, payload) => {
        const res = await fetch(`${API_URL}${url}`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
            body: JSON.stringify(payload),
        });
        return readApiJson(res, 'Request failed.');
    };

    const refresh = async () => { await load(); onChanged?.(); };

    const toggleExpanded = (id) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const addCategory = async (name) => {
        await postJson('/api/legal/categories/save/', { name });
        setAddingCategory(false);
        await refresh();
    };

    const renameCategory = async (id, name) => {
        await postJson('/api/legal/categories/save/', { id, name });
        setEditingCategoryId(null);
        await refresh();
    };

    const deleteCategory = async (cat) => {
        if (!window.confirm(`Delete category "${cat.name}"? This also deletes its ${cat.subcategories.length} sub-categor${cat.subcategories.length === 1 ? 'y' : 'ies'}.`)) return;
        setDeleteCategoryBusyId(cat.id); setError('');
        try {
            await postJson('/api/legal/categories/save/', { delete_id: cat.id });
            await refresh();
        } catch (err) {
            setError(err.message);
        } finally {
            setDeleteCategoryBusyId(null);
        }
    };

    const addSubcategory = async (categoryId, name) => {
        await postJson('/api/legal/subcategories/save/', { category_id: categoryId, name });
        setAddingSubFor(null);
        await refresh();
    };

    const renameSubcategory = async (id, name) => {
        await postJson('/api/legal/subcategories/save/', { id, name });
        setEditingSubId(null);
        await refresh();
    };

    const deleteSubcategory = async (sub) => {
        if (!window.confirm(`Delete sub-category "${sub.name}"?`)) return;
        setDeleteSubBusyId(sub.id); setError('');
        try {
            await postJson('/api/legal/subcategories/save/', { delete_id: sub.id });
            await refresh();
        } catch (err) {
            setError(err.message);
        } finally {
            setDeleteSubBusyId(null);
        }
    };

    const q = search.trim().toLowerCase();
    const visibleCategories = q
        ? categories.filter(c => c.name.toLowerCase().includes(q) || c.subcategories.some(s => s.name.toLowerCase().includes(q)))
        : categories;

    if (!canManageData) return null;

    return (
        <div className="legal-modal-bg" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="legal-modal legal-modal-wide">
                <div className="legal-modal-hdr">
                    <div className="legal-modal-icon">🗂️</div>
                    <div>
                        <div className="legal-modal-title">Manage Categories</div>
                        <div className="legal-modal-sub">Document Category / Sub-Category taxonomy — changes apply everywhere immediately</div>
                    </div>
                    <button className="legal-modal-close" onClick={onClose} style={{ marginLeft: 'auto' }}>✕</button>
                </div>
                <div className="legal-modal-body">
                    {error && <div className="legal-form-error">{error}</div>}

                    <div className="legal-cat-toolbar">
                        <input
                            className="legal-search legal-cat-search"
                            placeholder="Search categories or sub-categories…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                        <button type="button" className="legal-btn-save legal-cat-add-btn" onClick={() => { setAddingCategory(true); setEditingCategoryId(null); }}>
                            <PlusIcon /> Add Category
                        </button>
                    </div>

                    {addingCategory && (
                        <div className="legal-cat-card legal-cat-card-new">
                            <InlineNameField placeholder="New category name" autoFocus onCancel={() => setAddingCategory(false)} onSubmit={addCategory} />
                        </div>
                    )}

                    {loading ? <p style={{ color: '#65676b', marginTop: 12 }}>Loading…</p> : (
                        <div className="legal-cat-list">
                            {visibleCategories.map(cat => (
                                <CategoryRow
                                    key={cat.id}
                                    cat={cat}
                                    expanded={expandedIds.has(cat.id) || Boolean(q)}
                                    onToggle={() => toggleExpanded(cat.id)}
                                    editing={editingCategoryId === cat.id}
                                    onStartEdit={() => { setEditingCategoryId(cat.id); setAddingCategory(false); }}
                                    onCancelEdit={() => setEditingCategoryId(null)}
                                    onRename={name => renameCategory(cat.id, name)}
                                    onDelete={() => deleteCategory(cat)}
                                    deleteBusy={deleteCategoryBusyId === cat.id}
                                    addingSub={addingSubFor === cat.id}
                                    onStartAddSub={() => { setAddingSubFor(cat.id); setEditingSubId(null); }}
                                    onCancelAddSub={() => setAddingSubFor(null)}
                                    onAddSub={name => addSubcategory(cat.id, name)}
                                    editingSubId={editingSubId}
                                    onStartEditSub={id => { setEditingSubId(id); setAddingSubFor(null); }}
                                    onCancelEditSub={() => setEditingSubId(null)}
                                    onRenameSub={renameSubcategory}
                                    onDeleteSub={deleteSubcategory}
                                    deleteSubBusyId={deleteSubBusyId}
                                />
                            ))}
                            {!visibleCategories.length && (
                                <div className="legal-drill-empty">
                                    {q ? `No categories match "${search.trim()}".` : 'No categories yet — add one to get started.'}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export { ManageCategoriesModal };
export default ManageCategoriesModal;
